'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { AlignLeft, Smartphone, X } from 'lucide-react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from '@eliadmin/core/src/components/ui/sheet';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '../ui/button';
import { NavLink } from '../ui/nav-link';

/**
 * Mobilné menu — Figma `60042:9400` (zatvorené) a `60042:9368` (otvorené).
 *
 * Override e-shopového `MobileSidebar` z jadra: ten vysúva kategórie
 * v accordione, kontaktné atribúty a sociálne ikony. KAPYCAR je prezentačný
 * web, takže obsah je iný — navigácia, telefón a CTA.
 *
 * Vysúva sa `side="top"`, teda cez celú šírku zhora — vo Figme panel prekryje
 * hlavičku a menu ikona sa zmení na krížik. Preto je vo vysunutom obsahu aj
 * vlastná horná lišta (krížik + logo): prekryje skutočnú hlavičku a je to to,
 * čo dizajn ukazuje.
 *
 * Rozmery odmerané z Figmy:
 *   lišta   58 vysoká · vonkajšie odsadenie 12 · vnútro so zvislými linkami,
 *           px 12 py 16 · krížik 26 · medzera 16 · logo 132×21
 *   panel   px 12 py 32 · medzera 32 · odkazy pod sebou centrované s medzerou 16
 *           telefón (ikona 26 + medzera 8) · tlačidlo na plnú šírku, 48 vysoké
 *
 * Vlastný krížik namiesto zabudovaného (`showCloseIcon={false}`): ten z jadra
 * sedí absolútne v rohu, kým Figma ho má v riadku s logom.
 *
 * Farby: jediná premenná je `primary`, zvyšok natvrdo hexom.
 */

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: { type?: 'page'; name: string; path?: string }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];

  /* KAPYCAR — obsah, ktorý e-shopová verzia nepozná. */
  phone?: string;
  logoUrl?: string;
  appName?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

const MobileSidebar = ({
  side = 'top',
  menu = [],
  phone,
  logoUrl,
  appName = 'KAPYCAR',
  ctaLabel = 'Cenová ponuka',
  ctaHref = '/#kontakt',
  className,
}: MobileSidebarProps) => {
  const pathname = usePathname();

  const isActive = (path?: string) => {
    // Kotvy (#o-nas, #sluzby …) sa podľa cesty rozlíšiť nedajú — aktívna by
    // vyžadovala scroll-spy. Zvýrazňuje sa preto len skutočná zhoda cesty.
    if (!path || path.startsWith('#')) return false;
    return pathname === path || pathname === `/${path.replace(/^\//, '')}`;
  };

  const telHref = phone ? `tel:${phone.replace(/\s/g, '')}` : undefined;

  return (
    <Sheet>
      <SheetTrigger
        aria-label="Otvoriť menu"
        className={cn(
          'ea-mobile-sidebar__trigger',
          'text-[#212121] transition-opacity hover:opacity-70',
          className?.trigger,
        )}
      >
        <AlignLeft className="size-[26px]" strokeWidth={2} aria-hidden="true" />
      </SheetTrigger>

      <SheetContent
        side={side}
        showCloseIcon={false}
        className={cn(
          'ea-mobile-sidebar__content',
          // Hlavička je `fixed z-50`, panel ju musí prekryť.
          'z-[60] flex flex-col gap-0 border-b-0 bg-[#F6F1EA] p-0',
          className?.content,
        )}
      >
        <SheetTitle className="sr-only">Menu {appName}</SheetTitle>
        <SheetDescription className="sr-only">Navigácia a kontaktné údaje</SheetDescription>
        {/* Horná lišta — vo Figme nahradí hlavičku: krížik namiesto menu ikony,
            telefón vpravo zmizne. */}
        <div className="custom-section">
          <div className="custom-container flex items-center py-4">
            <div className="flex items-center gap-4">
              <SheetClose
                aria-label="Zavrieť menu"
                className="text-[#212121] transition-opacity hover:opacity-70"
              >
                <X className="size-[26px]" strokeWidth={2} aria-hidden="true" />
              </SheetClose>

              <Brand logoUrl={logoUrl} appName={appName} />
            </div>
          </div>
        </div>

        <div className="custom-section">
          <div className="custom-container flex flex-col items-center gap-6 py-8 sm:gap-8">
            <nav className={cn('flex flex-col items-center gap-4', className?.menuList)}>
              {menu.map(item => (
                <SheetClose key={item.path ?? item.name} asChild>
                  <NavLink
                    title={item.name}
                    href={item.path ?? '#'}
                    active={isActive(item.path)}
                    className={className?.menuLink}
                  />
                </SheetClose>
              ))}
            </nav>

            {!!phone && (
              <SheetClose asChild>
                <Link
                  href={telHref!}
                  className="flex items-center gap-2 text-[#212121] transition-opacity hover:opacity-70"
                >
                  <Smartphone
                    className="size-[26px] shrink-0 text-primary"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <span className="nav-link whitespace-nowrap">{phone}</span>
                </Link>
              </SheetClose>
            )}

            <SheetClose asChild>
              <Button
                asChild
                variant="brand-accent"
                size="brand"
                rounded="pill"
                className={cn('w-full', className?.button)}
              >
                <Link href={ctaHref}>{ctaLabel}</Link>
              </Button>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/** Logo z administrácie; kým tam nie je, textový nápis ako v hlavičke. */
function Brand({ logoUrl, appName }: { logoUrl?: string; appName: string }) {
  return (
    <SheetClose asChild>
      <Link href={ROUTES.HOME()} className="flex items-center transition-opacity hover:opacity-75">
        {logoUrl ? (
          <span className="relative block h-[21px] w-[132px]">
            <Image
              src={logoUrl}
              alt={appName}
              fill
              sizes="132px"
              className="object-contain object-left"
            />
          </span>
        ) : (
          <span className="text-[21px] font-black uppercase leading-none tracking-[-0.02em] text-[#212121]">
            Kapycar<span className="text-primary">.</span>
          </span>
        )}
      </Link>
    </SheetClose>
  );
}

export default MobileSidebar;
